import { Box, Drawer, IconButton, Modal, Typography, useMediaQuery, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { PropsWithChildren, ReactNode } from 'react';
import { Z_INDEX } from '@/styles/zindex';

interface CompleteDataDialogShellProps {
	open: boolean;
	title: string;
	description?: ReactNode;
	hideCloseButton?: boolean;
	onClose: () => void;
}

const TITLE_FONT = 'normal normal 800 24px/33px Open Sans';
const DESCRIPTION_COLOR = '#575757';

export const CompleteDataDialogShell: React.FC<PropsWithChildren<CompleteDataDialogShellProps>> = ({
	open,
	title,
	description,
	hideCloseButton = false,
	onClose,
	children,
}) => {
	const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));

	const closeButton = !hideCloseButton ? (
		<IconButton
			color="inherit"
			onClick={onClose}
			sx={{
				position: 'absolute',
				right: '15px',
				top: '15px',
				'&:hover': { backgroundColor: 'transparent', color: 'inherit' },
			}}
		>
			<ClearIcon color="inherit" fontSize="inherit" />
		</IconButton>
	) : null;

	if (isMobile) {
		return (
			<Drawer
				anchor="bottom"
				open={open}
				keepMounted
				onClose={onClose}
				sx={{
					zIndex: Z_INDEX.AUTH_FLOW_ALERT,
					'& .MuiDrawer-paper': { borderRadius: '8px 8px 0 0', overflow: 'hidden' },
				}}
			>
				<Box sx={{ position: 'relative', padding: '30px 20px', maxHeight: '85dvh', overflow: 'auto' }}>
					{closeButton}
					<Typography component="h1" sx={{ font: TITLE_FONT, marginBottom: '8px' }}>
						{title}
					</Typography>
					{description ? (
						<Typography
							sx={{ fontSize: 'var(--font-size-body)', color: DESCRIPTION_COLOR, marginBottom: '20px' }}
						>
							{description}
						</Typography>
					) : null}
					{children}
				</Box>
			</Drawer>
		);
	}

	return (
		<Modal open={open} onClose={onClose} sx={{ padding: 0, zIndex: Z_INDEX.AUTH_FLOW_ALERT }}>
			<Box
				sx={{
					backgroundColor: 'white',
					width: '90vw',
					maxWidth: '585px',
					maxHeight: '90vh',
					overflow: 'auto',
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					borderRadius: '6px',
					padding: '25.5px 30px',
					outline: 'none',
				}}
			>
				{closeButton}
				<Typography
					component="h1"
					sx={{ font: TITLE_FONT, textAlign: 'center', marginBottom: '8px' }}
				>
					{title}
				</Typography>
				{description ? (
					<Typography
						sx={{
							fontSize: 'var(--font-size-body)',
							color: DESCRIPTION_COLOR,
							textAlign: 'center',
							marginBottom: '20px',
						}}
					>
						{description}
					</Typography>
				) : null}
				{children}
			</Box>
		</Modal>
	);
};
